import { formatNauticalMiles, M_PER_NM } from './unitConversion.ts';

/**
 * Ranking place suggestions by distance from an origin, and the line under each that says
 * how far. The origin is the home port, or the device when there is none.
 *
 * `.ts` import: `node --test` runs this file and cannot resolve `@/`.
 */

// ponytail: the saved home port has no country field, so "same region / same country" is
// approximated by distance from the origin. A country on the home port is the upgrade path.
/** Under this, a place is at the origin. */
const AT_ORIGIN_M = 0.5 * M_PER_NM;
/** Within a day's run of the origin. */
const NEAR_M = 25 * M_PER_NM;
/** Roughly the same coast or country. */
const REGION_M = 250 * M_PER_NM;

/** 0 at the origin, 1 near, 2 the region, 3 the rest or no distance known. */
export const distanceTier = (distanceMeters?: number) => {
  if (distanceMeters == null) return 3;
  if (distanceMeters < AT_ORIGIN_M) return 0;
  if (distanceMeters <= NEAR_M) return 1;
  if (distanceMeters <= REGION_M) return 2;
  return 3;
};

/** Nearer tiers first; `sort` is stable, so the order inside a tier is the one given. */
export const rankByDistance = <T extends { distanceMeters?: number }>(items: T[]) =>
  [...items].sort((a, b) => distanceTier(a.distanceMeters) - distanceTier(b.distanceMeters));

/**
 * A prediction's distance from the origin. Google leaves the field out at 0 m, which is the
 * place the origin was copied from: the home port itself. Roads leave it out too, having no
 * single point.
 */
export const predictionDistance = (
  prediction: { distanceMeters?: number; types?: string[] },
  hasOrigin: boolean,
) =>
  prediction.distanceMeters ?? (hasOrigin && !prediction.types?.includes('route') ? 0 : undefined);

/** One fact on the line under a place; the picker draws `kind` as an icon before the text. */
export type HintPart = {
  kind: 'home' | 'type' | 'dates' | 'city' | 'distance' | 'rating' | 'source' | 'nomap';
  text: string;
};

/** How far a place is. Only 0 m is the home port: a café on its quay is not. */
export const distanceHint = (
  distanceMeters: number | undefined,
  from: DistanceFrom,
): HintPart | undefined => {
  if (distanceMeters === undefined) return undefined;
  if (distanceMeters === 0 && from === 'home port') return { kind: 'home', text: 'Home port' };
  const text =
    distanceTier(distanceMeters) === 0
      ? `Under 1 nm from ${from}`
      : `${formatNauticalMiles(distanceMeters / M_PER_NM)} from ${from}`;
  return { kind: 'distance', text };
};

export type DistanceFrom = 'home port' | 'you';

type Point = { latitude: number; longitude: number };

/**
 * Straight-line (great-circle) distance: a trip's path length, and a nearby place's distance,
 * which Nearby Search, unlike Autocomplete, does not send. The clamp keeps rounding past 1 out of `asin`.
 */
export const metersBetween = (a: Point, b: Point) => {
  const rad = Math.PI / 180;
  const h =
    Math.sin(((b.latitude - a.latitude) * rad) / 2) ** 2 +
    Math.cos(a.latitude * rad) *
      Math.cos(b.latitude * rad) *
      Math.sin(((b.longitude - a.longitude) * rad) / 2) ** 2;
  return 2 * 6_371_000 * Math.asin(Math.min(1, Math.sqrt(h)));
};

/** How many nearby places the empty box lists under the home port. */
const NEARBY_COUNT = 6;

/**
 * Nearest first, capped. From the home port, a place at the origin is dropped: under another
 * name it is the home port itself (Google calls Lighthouse Point Marina "Port 32"), and the
 * list already leads with that.
 */
export const nearestPlaces = <T extends { distanceMeters: number }>(
  items: T[],
  from: DistanceFrom,
) =>
  items
    .filter((p) => from === 'you' || distanceTier(p.distanceMeters) > 0)
    .sort((a, b) => a.distanceMeters - b.distanceMeters)
    .slice(0, NEARBY_COUNT);

/** The line under a nearby place: what it is, where, how far, and its Google rating. */
export const nearbyHint = (
  place: { typeLabel?: string; city?: string; distanceMeters: number; rating?: number },
  from: DistanceFrom,
) =>
  (
    [
      place.typeLabel ? { kind: 'type', text: place.typeLabel } : undefined,
      place.city ? { kind: 'city', text: place.city } : undefined,
      distanceHint(place.distanceMeters, from),
      place.rating !== undefined
        ? { kind: 'rating', text: `${place.rating.toFixed(1)} rating from Google` }
        : undefined,
    ] satisfies (HintPart | undefined)[]
  ).filter((part): part is HintPart => !!part);
